// src/Authentication/Register.js
import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader, User } from 'lucide-react';
import authService from './AuthService';
import arcLogo from '../images/arc-logo.png';

const Register = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (message) setMessage('');
  };

  // ─── Password policy ──────────────────────────────────────────────
  //
  // Tightened 2026-06-03 from the original 6-char minimum to a real
  // policy. Self-signup is currently disabled (membership paywall),
  // but the Register flow still exists for any future admin
  // provisioning path and password resets, so the rules need to be
  // enforced consistently.
  //
  // Rules:
  //   - 12 characters minimum (modern recommendation, beats most
  //     credential-stuffing dictionaries)
  //   - At least 1 uppercase, 1 lowercase, 1 number
  //   - Block the top ~30 most common passwords / patterns
  //     (the full list of "100 worst" all fail the length+complexity
  //     check anyway; this list catches passwords that technically
  //     meet the format but are still trivially guessable)
  const PASSWORD_MIN_LENGTH = 12;
  const COMMON_PASSWORDS = [
    "password", "Password1", "Password123", "Qwerty123", "Welcome1",
    "Admin123", "Letmein1", "Iloveyou1", "1Q2w3e4r5t", "Abcd1234",
    "P@ssw0rd", "P@ssword1", "Football1", "Baseball1", "Princess1",
    "Sunshine1", "Trustno1", "Monkey123", "Dragon123", "Master123",
    "Shadow123", "Superman1", "Batman123", "Jordan123", "Harley123",
    "Hunter123", "Buster123", "Soccer123", "Hockey123", "Killer123",
  ];

  // Check inclusion case-insensitively against the blocklist.
  const isCommonPassword = (pwd) => {
    const lower = pwd.toLowerCase();
    return COMMON_PASSWORDS.some((bad) => lower === bad.toLowerCase());
  };

  const passwordRules = {
    length:    formData.password.length >= PASSWORD_MIN_LENGTH,
    upper:     /[A-Z]/.test(formData.password),
    lower:     /[a-z]/.test(formData.password),
    number:    /\d/.test(formData.password),
    notCommon: formData.password.length > 0 && !isCommonPassword(formData.password),
  };
  const passwordMeetsPolicy = Object.values(passwordRules).every(Boolean);

  const validateForm = () => {
    if (!formData.displayName.trim()) { setError('Please enter your full name.'); return false; }
    if (!formData.email.trim()) { setError('Please enter your email address.'); return false; }
    if (formData.password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`);
      return false;
    }
    if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/\d/.test(formData.password)) {
      setError('Password must include an uppercase letter, a lowercase letter, and a number.');
      return false;
    }
    if (isCommonPassword(formData.password)) {
      setError('This password is too common. Pick something unique.');
      return false;
    }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const result = await authService.register(
        formData.email,
        formData.password,
        { displayName: formData.displayName.trim() }
      );
      if (result.success) {
        setMessage(result.message);
        if (onRegister) onRegister(result.user);
        setFormData({ displayName: '', email: '', password: '', confirmPassword: '' });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Strength meter — counts how many of the policy rules pass + a
  // length bonus past the minimum. Visual only; the real gate is
  // `passwordMeetsPolicy` in `validateForm`.
  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, text: '', color: 'bg-arc-line' };
    const rulesMet = [
      password.length >= PASSWORD_MIN_LENGTH,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      !isCommonPassword(password),
    ].filter(Boolean).length;
    const lengthBonus = password.length >= 16 ? 1 : 0;
    const score = rulesMet + lengthBonus; // 0–6

    if (score <= 2) return { strength: 25, text: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { strength: 60, text: 'Fair', color: 'bg-amber-500' };
    if (score === 5) return { strength: 85, text: 'Strong', color: 'bg-arc-deep' };
    return { strength: 100, text: 'Excellent', color: 'bg-arc-gold' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen mesh flex items-center justify-center p-4">
      <div className="surface-xl max-w-md w-full p-8 sm:p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <img
              src={arcLogo}
              alt="ARCrypto"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </div>
          <p className="eyebrow mb-3 text-arc-gold">Be DEFIant</p>
          <h1 className="headline text-3xl mb-2">
            Create your <span className="text-arc-gold">FIRE</span>Tracker
          </h1>
          <p className="text-arc-muted text-sm">Start tracking your path to financial sovereignty.</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-5 p-3 rounded-xl border border-arc-deep/40 bg-arc-deep/10 flex items-start gap-2.5">
            <AlertCircle className="text-arc-light flex-shrink-0 mt-0.5" size={18} />
            <span className="text-arc-light text-sm leading-relaxed">{error}</span>
          </div>
        )}

        {message && (
          <div className="mb-5 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2.5">
            <CheckCircle className="text-emerald-400 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-emerald-300 text-sm leading-relaxed">{message}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full Name */}
          <div>
            <label htmlFor="displayName" className="eyebrow block mb-2">Full name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Your full name"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="eyebrow block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@arcrypto.io"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="eyebrow block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className="w-full pl-10 pr-12 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim hover:text-arc-ink transition-colors"
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {formData.password && (
              <div className="mt-2.5">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">Strength</span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-muted">{passwordStrength.text}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>

                {/*
                 * Requirements checklist — gives the user actionable
                 * feedback while typing. Each item turns from a dot
                 * into a check the moment its rule passes.
                 */}
                <ul className="mt-3 space-y-1">
                  {[
                    { ok: passwordRules.length,    label: `At least ${PASSWORD_MIN_LENGTH} characters` },
                    { ok: passwordRules.upper,     label: 'One uppercase letter' },
                    { ok: passwordRules.lower,     label: 'One lowercase letter' },
                    { ok: passwordRules.number,    label: 'One number' },
                    { ok: passwordRules.notCommon, label: 'Not on the common-passwords list' },
                  ].map((r) => (
                    <li
                      key={r.label}
                      className={`text-[11px] flex items-center gap-2 transition-colors ${
                        r.ok ? 'text-emerald-300' : 'text-arc-dim'
                      }`}
                    >
                      <span className={`inline-flex w-3.5 h-3.5 items-center justify-center rounded-full ${
                        r.ok
                          ? 'bg-emerald-500/20 border border-emerald-500/40'
                          : 'bg-arc-line/40 border border-arc-line'
                      }`}>
                        {r.ok ? (
                          <CheckCircle size={9} strokeWidth={3} />
                        ) : (
                          <span className="w-1 h-1 rounded-full bg-arc-dim" />
                        )}
                      </span>
                      {r.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="eyebrow block mb-2">Confirm password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="w-full pl-10 pr-12 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim hover:text-arc-ink transition-colors"
                disabled={loading}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {formData.confirmPassword && (
              <div className="mt-2 flex items-center gap-1.5">
                {formData.password === formData.confirmPassword ? (
                  <>
                    <CheckCircle size={14} className="text-emerald-400" />
                    <span className="text-xs text-emerald-300">Passwords match</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={14} className="text-arc-light" />
                    <span className="text-xs text-arc-light">Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-tactile btn-gold w-full py-3 px-4 rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={18} />
                Creating account…
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-7 rule" />

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-arc-muted">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-arc-gold hover:text-arc-light font-medium transition-colors"
              disabled={loading}
            >
              Sign in
            </button>
          </p>
        </div>

        {/* Terms */}
        <p className="mt-5 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
          By creating an account, you agree to our{' '}
          <a href="#" className="text-arc-deep hover:text-arc-gold transition-colors">Terms</a>
          {' '}and{' '}
          <a href="#" className="text-arc-deep hover:text-arc-gold transition-colors">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default Register;
